
import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import { ThemeProvider } from './contexts/ThemeContext';
import Deposit from './pages/DepositPage';
import WithdrawRequest from './pages/WithdrawRequest';
import Transactions from './pages/TransactionsPage';
import Home, { CricketBettingHubPage } from './pages/HomePage';
import Navbar from './components/Navbar';
import { Toaster } from 'react-hot-toast';
import LobbyPage from './features/lobby/LobbyPage';
import RPSLobbyPage from "./features/rps/LobbyPage";
import CrashPage from "./features/crash/CrashGame";
import ChessLobbyPage from "./features/chess/ChessLobbyPage";
import OmiLobbyPage from "./features/omi/OmiLobbyPage";
import LotteryPage from "./features/lottery/LotteryPage";
import Profile from './pages/Profile';
import About from './pages/About';
import Terms from './pages/Terms';
import ResponsibleGaming from './pages/ResponsibleGaming';
import Contact from './pages/Contact';
import Disclaimer from './pages/Disclaimer';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AgentQueues from "./features/lottery/AgentQueues";
import { useLobby } from './features/lobby/useLobby';
import {
  doc,
  setDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  collection, query, where
} from "firebase/firestore";
import { db } from "./firebase";
import AuthModal from './components/AuthModal';

import AgentApply from "./features/lottery/AgentApply";
import AgentDashboard from "./features/lottery/AgentDashboard";
import AgentCardDetail from "./features/lottery/AgentCardDetail";
import MyTickets from "./features/lottery/MyTickets";
import MyTicketDetail from "./features/lottery/MyTicketDetail";
import PaymentWindow from "./features/lottery/PaymentWindow";
import Notifications from "./features/lottery/Notifications";
import OfferLanding from "./features/lottery/OfferLanding";
import AgentPaymentSettings from "./features/lottery/AgentPaymentSettings";
import AgentBankManager from "./features/lottery/AgentBankManager";
import AgentCardHistory from "./features/lottery/AgentCardHistory";
import AllGlobalWinners from "./features/lottery/AllGlobalWinners";
import AgentsDirectory from "./features/lottery/AgentsDirectory";
import AgentStore from "./features/lottery/AgentStore";
import PublicCardViewer from "./features/lottery/PublicCardViewer";
import PostLoginRedirector from "./components/PostLoginRedirector";
import QuotexLikeUI from "./pages/QuotexLikeUI";
import PayBundle from "./features/lottery/PayBundle";
import AgentWinners from "./features/lottery/AgentWinners";
import Payouts from "./features/lottery/Payouts";
import T20BettingPage from "./pages/T20BettingPage";
import SportsPage from "./pages/SportsPage";
import InHouseGamesPage from "./features/casino/InHouseGamesPage";
import InHouseGamePage from "./features/casino/InHouseGamePage";


const SEO_BASE_URL = "https://www.iqcage.com";

const SEO_MAP = {
  "/": {
    title: "IQ Cage T20 Betting & Crypto Skill Games | Crash, Lottery, Sports",
    description:
      "Bet on T20 match winner, next-ball runs, total runs and play crash game with USDT on IQ Cage. Real-time odds, fast payouts, and a pro betting UI.",
    keywords:
      "t20 betting, cricket betting app, next ball betting, crash game crypto, iq cage, iqcage, usdt betting",
  },
  "/crash": {
    title: "Crash Game Live Multipliers | IQ Cage",
    description:
      "Play IQ Cage Crash with live multiplier chart, auto cashout, and instant betting controls. Fair rounds, fast gameplay, and real-time action.",
    keywords: "crash game, crypto crash, live multiplier betting, casino crash",
  },
  "/lottery": {
    title: "Daily Lottery with USDT Rewards | IQ Cage",
    description:
      "Join IQ Cage daily lottery, buy numbers, track draws, and win USDT rewards. Simple, fast and transparent lottery rounds.",
    keywords: "daily lottery, online lottery usdt, crypto lottery",
  },
  "/casino": {
    title: "In-House Casino Originals | IQ Cage",
    description:
      "Play IQ Cage originals including limbo, mines, keno, roulette, slots and more in one fast in-house casino lobby.",
    keywords: "in house casino, limbo game, mines game, roulette mini, slots sprint, iq cage originals",
  },
  "/xogame": {
    title: "Tic Tac Toe for Real Money | IQ Cage",
    description: "Play competitive Tic Tac Toe and win USDT on IQ Cage. Challenge players live and climb skill-based leaderboards.",
    keywords: "tic tac toe betting, skill game usdt, iq cage games",
  },
  "/omi": {
    title: "Omi Multiplayer Tables | IQ Cage",
    description:
      "Create or join four-seat Omi tables with live room codes, team scoring, and wallet stakes on IQ Cage.",
    keywords: "omi game, card table, multiplayer card game, room code game, iq cage omi",
  },
  "/sports/t20-betting": {
    title: "T20 Betting Guide & Live Markets | IQ Cage",
    description:
      "Explore T20 betting markets on IQ Cage: match winner, next-ball runs, and total-runs lines with real-time odds and fast crypto-friendly gameplay.",
    keywords:
      "t20 betting guide, t20 cricket betting, live t20 odds, next ball betting, iq cage t20",
  },
};

function applySEO(pathname) {
  const conf = SEO_MAP[pathname] || SEO_MAP["/"];
  document.title = conf.title;

  const setMeta = (name, content, property = false) => {
    const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
    let el = document.head.querySelector(selector);
    if (!el) {
      el = document.createElement("meta");
      if (property) el.setAttribute("property", name);
      else el.setAttribute("name", name);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };

  setMeta("description", conf.description);
  setMeta("keywords", conf.keywords);
  setMeta("og:title", conf.title, true);
  setMeta("og:description", conf.description, true);
  setMeta("og:url", `${SEO_BASE_URL}${pathname}`, true);
  setMeta("twitter:title", conf.title);
  setMeta("twitter:description", conf.description);

  let canon = document.head.querySelector('link[rel="canonical"]');
  if (!canon) {
    canon = document.createElement("link");
    canon.setAttribute("rel", "canonical");
    document.head.appendChild(canon);
  }
  canon.setAttribute("href", `${SEO_BASE_URL}${pathname}`);

  const id = "iqcage-jsonld";
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    document.head.appendChild(script);
  }

  const primary = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "IQ Cage",
    url: SEO_BASE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SEO_BASE_URL}/?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SEO_BASE_URL}/` },
      ...(pathname !== "/" ? [{ "@type": "ListItem", position: 2, name: conf.title, item: `${SEO_BASE_URL}${pathname}` }] : []),
    ],
  };

  const routeSpecific = pathname === "/sports/t20-betting"
    ? {
        "@context": "https://schema.org",
        "@type": "SportsEvent",
        name: "T20 Betting Markets on IQ Cage",
        description: conf.description,
        eventStatus: "https://schema.org/EventScheduled",
        sport: "Cricket",
        organizer: { "@type": "Organization", name: "IQ Cage", url: SEO_BASE_URL },
        url: `${SEO_BASE_URL}${pathname}`,
      }
    : {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: conf.title,
        description: conf.description,
        url: `${SEO_BASE_URL}${pathname}`,
      };

  script.text = JSON.stringify([primary, breadcrumbs, routeSpecific]);
}

function useAffiliateId() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  return params.get("lid") || "";
}

function AppRoutes() {
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [balance, setBalance] = useState(0);
  const [userId, setUserId] = useState(null);
  const [busyUserIds, setBusyUserIds] = useState([]);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);

  // Auth Modal Controls
  const location = useLocation();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("login");
  const affiliateId = useAffiliateId();

  // Open modal based on URL
  useEffect(() => {
    if (location.pathname === "/register" || location.pathname === "/login") {
      setModalOpen(true);
      setModalMode(location.pathname === "/register" ? "register" : "login");
    } else {
      setModalOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    applySEO(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const goIfNeeded = () => {
      const target = sessionStorage.getItem("postLoginRedirect");
      if (!target) return;
      // Only redirect once the user is logged in
      // (You already track `user` via onAuthStateChanged)
      if (!user) return;

      // If we’re on /login (modal route) or / (home bounce), jump to target
      if (location.pathname === "/login" || location.pathname === "/") {
        sessionStorage.removeItem("postLoginRedirect");
        navigate(target, { replace: true });
      }
    };

    goIfNeeded();
  }, [user, location.pathname, navigate]);

  // Save user profile on auth change
  async function saveUserProfile(user, affiliateId) {
    if (!user) return;
    const data = {
      username: user.displayName || user.email,
      email: user.email,
      avatar: user.photoURL || "/user.png",
    };
    if (affiliateId) data.affiliateId = affiliateId;
    await setDoc(doc(db, "users", user.uid), data, { merge: true });
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
        setUserId(user.uid);
        saveUserProfile(user, affiliateId);
        const redirectTo = sessionStorage.getItem("postLoginRedirect");
        if (redirectTo) {
          sessionStorage.removeItem("postLoginRedirect");
          navigate(redirectTo);
        }
      } else {
        setUser(null);
        setUserId(null);
      }
    });
    return () => unsubscribe();
  }, [affiliateId, navigate]);

  useEffect(() => {
    // Listen for active games and extract busy userIds
    const gamesRef = collection(db, "games");
    const q = query(gamesRef, where("status", "==", "active"));
    const unsubscribe = onSnapshot(q, (snap) => {
      const busy = new Set();
      snap.forEach((doc) => {
        const g = doc.data();
        if (g.playerX?.userId) busy.add(g.playerX.userId);
        if (g.playerO?.userId) busy.add(g.playerO.userId);
      });
      setBusyUserIds([...busy]);
    });
    return () => unsubscribe();
  }, []);

  const {
    onlineUsers,
    invites,
    currentGameId,
    setCurrentGameId,
    handleInvite,
    handleAccept,
    handleDecline
  } = useLobby(user, balance);

  useEffect(() => {
    if (!userId) return;
    const userRef = doc(db, "users", userId);
    updateDoc(userRef, {
      online: true,
      lastActive: serverTimestamp(),
    });
    const handleOffline = () => updateDoc(userRef, { online: false });
    window.addEventListener("beforeunload", handleOffline);
    const interval = setInterval(() => {
      updateDoc(userRef, { lastActive: serverTimestamp(), online: true });
    }, 30000);
    return () => {
      clearInterval(interval);
      window.removeEventListener("beforeunload", handleOffline);
      updateDoc(userRef, { online: false });
    };
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const userDocRef = doc(db, "users", userId);
    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        setBalance(docSnap.data().balance || 0);
      }
    });
    return () => unsubscribe();
  }, [userId]);

  const handleSidebarToggle = () => setIsSidebarOpen((v) => !v);

  return (
    <>
      <Navbar
        isAuthenticated={!!user}
        balance={balance}
        onSidebarToggle={handleSidebarToggle}
        user={user}
        invites={invites}
        onNotifClick={() => setShowNotifDropdown(v => !v)}
        showNotifDropdown={showNotifDropdown}
        onAcceptInvite={handleAccept}
        onDeclineInvite={handleDecline}
      />
      <div className="flex flex-col md:flex-row min-h-screen w-full">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} user={user} />
        <main className="flex-1 min-w-0 overflow-x-hidden bg-[#1a1d23] text-black dark:text-white pt-28 md:pt-24">
          <PostLoginRedirector user={user} />
          <Routes>
            <Route path="/deposit" element={<Deposit user={user} />} />
            <Route path="/" element={<Home user={user} />} />
            <Route path="/about" element={<About />} />
            <Route path="/casino" element={<InHouseGamesPage user={user} balance={balance} />} />
            <Route path="/casino/:gameSlug" element={<InHouseGamePage user={user} balance={balance} />} />
            <Route path="/sports" element={<SportsPage />} />
            <Route path="/sports/cricket" element={<CricketBettingHubPage user={user} />} />
            <Route path="/sports/t20-betting" element={<T20BettingPage />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/responsible-gaming" element={<ResponsibleGaming />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/withdraw" element={<WithdrawRequest user={user} balance={balance} />} />
            <Route path="/crash" element={<CrashPage user={user} userBalance={balance} />} />
            <Route path="/lottery" element={<LotteryPage user={user} balance={balance} />} />
            <Route path="/transactions" element={<Transactions user={user} balance={balance} />} />
            <Route path="/profile" element={<Profile user={user} />} />

            <Route path="/lottery/pay/:ticketId" element={<PaymentWindow user={user} />} />
            <Route path="/my-tickets" element={<MyTickets user={user} />} />
            <Route path="/my-tickets/:ticketId" element={<MyTicketDetail user={user} />} />

            <Route path="/notifications" element={<Notifications user={user} />} />

            {/* Agent area */}
            <Route path="/agent/apply" element={<AgentApply user={user} />} />
            <Route path="/agent" element={<AgentDashboard user={user} />} />
            <Route path="/agent/cards/:cardId" element={<AgentCardDetail user={user} />} />

            <Route path="/lottery/offer" element={<OfferLanding user={user} balance={balance} />} />
            <Route path="/agent/settings" element={<AgentPaymentSettings user={user} />} />
            <Route path="/agent/banks" element={<AgentBankManager user={user} />} />
            <Route path="/agent/history" element={<AgentCardHistory user={user} />} />
            <Route path="/winners" element={<AllGlobalWinners />} />
            <Route path="/agents" element={<AgentsDirectory user={user} />} />
            <Route path="/agents/:agentId" element={<AgentStore user={user} />} />
            <Route path="/card/:cardId" element={<PublicCardViewer user={user} />} />
            <Route path="/agent/queues/:cardId" element={<AgentQueues user={user} />} />
            <Route path="/lottery/pay/bundle/:bundleKey" element={<PayBundle user={user} />} />
            <Route path="/agent/winners" element={<AgentWinners user={user} />} />
            <Route path="/lottery/Payouts" element={<Payouts user={user} />} />


            <Route path="/qx" element={<QuotexLikeUI />} />

            <Route
              path="/xogame"
              element={
                <LobbyPage
                  onlineUsers={onlineUsers}
                  invites={invites}
                  onInvite={handleInvite}
                  onAccept={handleAccept}
                  onDecline={handleDecline}
                  currentGameId={currentGameId}
                  user={user}
                  setCurrentGameId={setCurrentGameId}
                  busyUserIds={busyUserIds}
                  balance={balance}
                />
              }
            />
            <Route
              path="/rps"
              element={
                <RPSLobbyPage
                  onlineUsers={onlineUsers}
                  invites={invites}
                  onInvite={handleInvite}
                  onAccept={handleAccept}
                  onDecline={handleDecline}
                  currentGameId={currentGameId}
                  user={user}
                  setCurrentGameId={setCurrentGameId}
                  busyUserIds={busyUserIds}
                  balance={balance}
                />
              }
            />

            <Route
              path="/chess"
              element={
                <ChessLobbyPage
                  onlineUsers={onlineUsers}
                  invites={invites}
                  onInvite={handleInvite}
                  onAccept={handleAccept}
                  onDecline={handleDecline}
                  currentGameId={currentGameId}
                  user={user}
                  setCurrentGameId={setCurrentGameId}
                  busyUserIds={busyUserIds}
                  balance={balance}
                />
              }
            />
            <Route path="/omi" element={<OmiLobbyPage user={user} />} />
          </Routes>

        </main>
      </div>
      <AuthModal
        isOpen={modalOpen}
        onClose={() => {
          const redirectTo = sessionStorage.getItem("postLoginRedirect");
          if (redirectTo) {
            sessionStorage.removeItem("postLoginRedirect");
            navigate(redirectTo, { replace: true });
          } else {
            navigate("/");
          }
        }}
        initialMode={modalMode}
        affiliateId={affiliateId}
      />
    </>
  );
}

// Wrap with Router/ThemeProvider/Toaster at root
export default function App() {
  return (
    <ThemeProvider>
      <Toaster position="top-center" />
      <Router>
        <AppRoutes />
      </Router>
    </ThemeProvider>
  );
}
